import { renderToString } from 'react-dom/server';
import { formatEmail } from '../../../utils';
import imageCompression from 'browser-image-compression';
import { ICorpStation, IUser, IVisualReporting } from '../../../globals/interfaces';
import GenerateDamageReport from './GenerateDamageReport';
import apiClient from '../../../apiClient';

export interface IDamageReportItem {
    user_submitted: string;
    collection_id: string;
    awb_uld: string;
    full_name: string;
    comments: string;
    time_submitted: string;
    file_links: Array<string>;
}

export type DamageReportMap = Record<string, IDamageReportItem>;

const getFileSize = async (file_link: string): Promise<number> => {
    const response = await fetch(file_link);
    // Convert the fetched data to a Blob
    const imgBlob = await response.blob();
    return imgBlob.size;
}

async function compressImageFromUrl(url: string) {
    // Fetch the image data from the URL
    const response = await fetch(url);

    // Convert the fetched data to a Blob
    const imgBlob = await response.blob();

    const options = {
        maxSizeMB: 1, // (default: Number.POSITIVE_INFINITY)
        maxWidthOrHeight: 1920, // (default: undefined)
        useWebWorker: true, // (default: true)
        maxIteration: 10, // (default: 10)
    };

    try {
        // Pass the Blob to the imageCompression function
        // @ts-ignore
        const compressedFile = await imageCompression(imgBlob, options);
        // This will return a Blob object which is smaller in size.
        return compressedFile;
    } catch (error) {
        console.log(error);
    }
}

export const generateDamageReport = async (
    generateType: 'print' | 'email',
    user: IUser,
    userStation: ICorpStation | undefined,
    createdBy: string,
    createdAt: string,
    comments: string,
    images: Array<string>,
    title: string,
    reportType: 'DAMAGE' | 'RAMP', 
    collection_id?: string, 
    flightNumber?: string,
    flightDate?: string,
    type?: string,
    setLoading?: React.Dispatch<React.SetStateAction<boolean>>,
) => {

    const useCreatedBy = formatEmail(createdBy);

    if (setLoading) {
        setLoading(true);
    }
    const urlsArray = [];

    for (const file_link of images) {
        const size = await getFileSize(file_link);
        if (size > 400000) {
            const blob = await compressImageFromUrl(file_link);
            // @ts-ignore
            const reducedUrl = URL.createObjectURL(blob);
            urlsArray.push(reducedUrl);
        } else {
            urlsArray.push(file_link);
        }
    }

    const damageReportPrint = renderToString(
        <GenerateDamageReport
            reportType={reportType}
            userStation={userStation}
            createdBy={useCreatedBy}
            createdAt={createdAt}
            comments={comments}
            images={images}
            title={title}
        />
    );

    if (setLoading) {
        setLoading(false);
    }
    if (generateType === 'print') {
        const myWindow = window.open('', 'MsgWindow', 'width=1920,height=1080');
        // @ts-ignore
        myWindow.document.write(damageReportPrint);
    } else {
        emailDamageReport(
            user, 
            reportType, 
            createdBy,
            createdAt,
            comments,
            title,
            collection_id, 
            flightNumber, 
            flightDate, 
            type
        );
    }
};

export const emailDamageReport = async (
    user: IUser, 
    reportType: 'DAMAGE' | 'RAMP', 
    createdBy: string,
    createdAt: string,
    comments: string,
    title: string,
    collection_id?: string, 
    flightNumber?: string,
    flightDate?: string,
    type?: string
) => {

    const data = {
        reportType,
        createdBy,
        createdAt,
        comments,
        title,
        collection_id,
        flightNumber,
        flightDate,
        type,
        userEmail: user.s_email,
    };

    await apiClient.post('/emailVisualReport', { data });
    alert('You will receive an email');
};

export const getDamageReportMapKey = (item: IVisualReporting) => {
    const key = `${item.collection_id}|${item.awb_uld}`;
    return key;
}

export const getDamageReportMap = (data: Array<IVisualReporting>): DamageReportMap => {
    const map: Record<string, IDamageReportItem> = {};
    for (const item of data) {
        const { user_submitted, collection_id, awb_uld, full_name, comments, time_submitted, file_link } = item;
        const key = getDamageReportMapKey(item);
        if (!map[key]) {
            map[key] = {
                user_submitted,
                collection_id, 
                awb_uld, 
                full_name, 
                comments, 
                time_submitted,
                file_links: []
            }
        }
        map[key].file_links.push(file_link);
    }
    return map;
}